import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FiShoppingCart, FiUsers, FiBox, FiRotateCcw, FiDownload } from 'react-icons/fi'
import { api, ROUTES, imgUrl } from './api'
import { useAsync, PageHead, Loading, money, Empty } from './ui'

/* ---------------------------------------------------
   FIELD NAMES - if your API uses other names, change them here only
--------------------------------------------------- */
const DAY = 86400000
const orderTime = (o) => new Date(o.createdAt ?? o.created_at ?? 0).getTime()
const orderTotal = (o) => Number(o.total ?? 0)
const orderCustomer = (o) => o.userId ?? o.user_id ?? o.customerEmail ?? o.email ?? null
const orderChannel = (o) => o.channel ?? o.source ?? 'Website'
const orderDiscount = (o) => Number(o.discount ?? o.discountAmount ?? 0)
const orderTax = (o) => Number(o.tax ?? o.taxAmount ?? 0)
const orderShipping = (o) => Number(o.shipping ?? o.shippingCharge ?? o.shippingCharges ?? 0)
const orderItems = (o) => (Array.isArray(o.items) ? o.items : [])

const COLORS = {
  accent: '#ff6b4a',
  blue: '#3b82f6',
  green: '#22b35e',
  orange: '#f59e0b',
  red: '#ef4444',
  gray: '#9ca3af',
}
const STATUS_COLORS = {
  Delivered: COLORS.green,
  Processing: COLORS.blue,
  Shipped: COLORS.orange,
  Cancelled: COLORS.red,
}
const CHANNEL_COLORS = [COLORS.blue, COLORS.green, COLORS.orange, COLORS.gray]

/* ---------------------------------------------------
   HELPERS
--------------------------------------------------- */
const startOfDay = (d) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x.getTime()
}
const fmtDay = (t) => new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
const fmtLong = (t) => new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const pct = (cur, prev) => (prev > 0 ? ((cur - prev) / prev) * 100 : null)

const compact = (n) => {
  if (n >= 100000) return `₹${+(n / 100000).toFixed(1)}L`
  if (n >= 1000) return `₹${+(n / 1000).toFixed(1)}k`
  return `₹${Math.round(n)}`
}

const niceMax = (n) => {
  if (n <= 0) return 4
  const p = 10 ** Math.floor(Math.log10(n))
  const m = n / p
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p
}

const downloadCsv = (rows, filename) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const csv = rows.map((r) => r.map(esc).join(',')).join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/* ---------------------------------------------------
   TAILWIND CLASS SHORTCUTS
--------------------------------------------------- */
const card = 'bg-white border border-[#e8ebef] rounded-xl p-4 sm:p-5 min-w-0'
const cardTitle = 'text-[15px] font-semibold text-[#1f2937] mb-3.5'
const selectCls =
  'h-[38px] px-3 border border-[#e8ebef] rounded-lg bg-white text-[13px] text-[#1f2937] cursor-pointer focus:outline-none focus:border-[#ff6b4a]'
const linkBtn =
  'block text-center mt-4 py-2.5 border border-[#e8ebef] rounded-lg text-[#ff6b4a] text-[13px] font-medium hover:bg-[#fff5f2] transition-colors'

const TONES = {
  blue: 'bg-[#e8f0fe] text-[#3b82f6]',
  green: 'bg-[#e6f6ec] text-[#22b35e]',
  purple: 'bg-[#efeafd] text-[#7c5cdb]',
  amber: 'bg-[#fff3e1] text-[#f59e0b]',
  red: 'bg-[#feeceb] text-[#ef4444]',
}

const RupeeIcon = () => <b>₹</b>

/* ---------------------------------------------------
   SMALL PARTS
--------------------------------------------------- */
function Kpi({ icon: Icon, tone, label, value, change, goodWhenDown, prevLabel }) {
  const hasChange = change !== null && change !== undefined && Number.isFinite(change)
  const up = hasChange && change >= 0
  const good = goodWhenDown ? !up : up
  return (
    <div className='flex items-center gap-3.5 bg-white border border-[#e8ebef] rounded-xl p-4 min-w-0'>
      <span className={`w-[46px] h-[46px] rounded-full grid place-items-center text-[20px] shrink-0 ${TONES[tone]}`}>
        <Icon />
      </span>
      <div className='min-w-0'>
        <p className='text-[12px] text-[#6b7280]'>{label}</p>
        <p className='text-[22px] font-bold text-[#1f2937] my-0.5 whitespace-nowrap'>{value}</p>
        {hasChange ? (
          <p className={`text-[11px] font-semibold ${good ? 'text-[#22b35e]' : 'text-[#ef4444]'}`}>
            {up ? '↑' : '↓'} {Math.abs(change).toFixed(1)}%{' '}
            <span className='font-normal text-[#6b7280]'>vs {prevLabel}</span>
          </p>
        ) : (
          <p className='text-[11px] text-[#6b7280]'>No earlier data to compare</p>
        )}
      </div>
    </div>
  )
}

function LineChart({ points }) {
  const W = 700
  const H = 260
  const pad = { l: 56, r: 40, t: 14, b: 30 }
  const iw = W - pad.l - pad.r
  const ih = H - pad.t - pad.b
  const maxRev = niceMax(Math.max(...points.map((p) => p.revenue), 0))
  const maxOrd = niceMax(Math.max(...points.map((p) => p.orders), 0))
  const x = (i) => pad.l + (points.length === 1 ? iw / 2 : (i / (points.length - 1)) * iw)
  const yRev = (v) => pad.t + ih - (v / maxRev) * ih
  const yOrd = (v) => pad.t + ih - (v / maxOrd) * ih
  const every = Math.ceil(points.length / 8)
  const line = (fn, key) => points.map((p, i) => `${x(i)},${fn(p[key])}`).join(' ')

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className='w-full h-auto block' role='img' aria-label='Revenue and orders over time'>
      {[0, 1, 2, 3, 4].map((t) => {
        const y = pad.t + ih - (t / 4) * ih
        return (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y} y2={y} className='stroke-[#eef0f3]' strokeWidth='1' />
            <text x={pad.l - 8} y={y + 4} textAnchor='end' className='fill-[#8a93a0] text-[10px]'>
              {compact((maxRev / 4) * t)}
            </text>
            <text x={W - pad.r + 8} y={y + 4} className='fill-[#8a93a0] text-[10px]'>
              {Math.round((maxOrd / 4) * t)}
            </text>
          </g>
        )
      })}

      <polyline points={line(yRev, 'revenue')} fill='none' stroke={COLORS.accent} strokeWidth='2' />
      <polyline points={line(yOrd, 'orders')} fill='none' stroke={COLORS.blue} strokeWidth='2' />

      {points.map((p, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={yRev(p.revenue)} r='3.5' fill={COLORS.accent}>
            <title>{`${p.label}: ${money(p.revenue)}`}</title>
          </circle>
          <circle cx={x(i)} cy={yOrd(p.orders)} r='3.5' fill={COLORS.blue}>
            <title>{`${p.label}: ${p.orders} orders`}</title>
          </circle>
          {i % every === 0 && (
            <text x={x(i)} y={H - 8} textAnchor='middle' className='fill-[#8a93a0] text-[10px]'>
              {p.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  )
}

function Donut({ segments, total, caption }) {
  const R = 70
  const C = 2 * Math.PI * R
  const sum = segments.reduce((s, x) => s + x.value, 0)
  let offset = 0
  return (
    <svg viewBox='0 0 200 200' className='w-[140px] h-[140px] sm:w-[170px] sm:h-[170px] shrink-0' role='img' aria-label={caption}>
      <circle cx='100' cy='100' r={R} fill='none' stroke='#f1f3f5' strokeWidth='30' />
      {sum > 0 &&
        segments.map((s) => {
          const len = (s.value / sum) * C
          const el = (
            <circle
              key={s.label}
              cx='100'
              cy='100'
              r={R}
              fill='none'
              stroke={s.color}
              strokeWidth='30'
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              transform='rotate(-90 100 100)'
            />
          )
          offset += len
          return el
        })}
      <text x='100' y='98' textAnchor='middle' className='fill-[#1f2937] text-[17px] font-bold'>
        {total}
      </text>
      <text x='100' y='118' textAnchor='middle' className='fill-[#6b7280] text-[11px]'>
        {caption}
      </text>
    </svg>
  )
}

function Legend({ rows }) {
  return (
    <ul className='grid gap-3.5 text-[13px]'>
      {rows.map((r) => (
        <li key={r.label} className='flex items-start'>
          <i className='inline-block w-2.5 h-2.5 rounded-full mr-1.5 mt-1 shrink-0' style={{ background: r.color }} />
          <div>
            <p className='text-[#6b7280] mb-0.5'>{r.label}</p>
            <strong className='font-semibold text-[#1f2937]'>{r.value}</strong>{' '}
            <span className='text-[#6b7280]'>({r.share})</span>
          </div>
        </li>
      ))}
    </ul>
  )
}

/* ---------------------------------------------------
   PAGE
--------------------------------------------------- */
export default function Reports() {
  const [days, setDays] = useState(7)
  const [grain, setGrain] = useState('daily')

  const { data, loading } = useAsync(async () => {
    const [p, o] = await Promise.allSettled([api.get(ROUTES.products), api.get(ROUTES.adminOrdersAll)])
    return {
      products: p.status === 'fulfilled' ? p.value : [],
      orders: o.status === 'fulfilled' ? o.value : null,
    }
  })

  const stats = useMemo(() => {
    if (!data?.orders) return null
    const end = startOfDay(Date.now()) + DAY
    const start = end - days * DAY
    const prevStart = start - days * DAY

    const within = (o, a, b) => {
      const t = orderTime(o)
      return t >= a && t < b
    }
    const cur = data.orders.filter((o) => within(o, start, end))
    const prev = data.orders.filter((o) => within(o, prevStart, start))
    const live = (list) => list.filter((o) => o.status !== 'Cancelled')
    const sum = (list, fn) => list.reduce((s, o) => s + fn(o), 0)
    const customers = (list) => new Set(list.map(orderCustomer).filter(Boolean)).size
    const returns = (list) => list.filter((o) => /return/i.test(o.status || '')).length

    const calc = (list) => {
      const revenue = sum(live(list), orderTotal)
      const orders = list.length
      return {
        revenue,
        orders,
        customers: customers(list),
        aov: live(list).length ? revenue / live(list).length : 0,
        returnRate: orders ? (returns(list) / orders) * 100 : 0,
      }
    }
    const c = calc(cur)
    const p = calc(prev)

    // chart buckets (daily or weekly)
    const step = grain === 'weekly' ? 7 : 1
    const points = Array.from({ length: Math.ceil(days / step) }, (_, i) => {
      const from = start + i * step * DAY
      const to = Math.min(from + step * DAY, end)
      const list = cur.filter((o) => within(o, from, to))
      return { label: fmtDay(from), revenue: sum(live(list), orderTotal), orders: list.length }
    })

    // order status
    const statusCount = {}
    cur.forEach((o) => {
      const s = o.status || 'Other'
      statusCount[s] = (statusCount[s] || 0) + 1
    })
    const order = ['Delivered', 'Processing', 'Shipped', 'Cancelled']
    const statuses = [
      ...order.filter((s) => statusCount[s]),
      ...Object.keys(statusCount).filter((s) => !order.includes(s)),
    ].map((s) => ({ label: s, value: statusCount[s], color: STATUS_COLORS[s] || COLORS.gray }))

    // sales by channel
    const channelSum = {}
    live(cur).forEach((o) => {
      const ch = orderChannel(o)
      channelSum[ch] = (channelSum[ch] || 0) + orderTotal(o)
    })
    const channels = Object.entries(channelSum)
      .sort((a, b) => b[1] - a[1])
      .map(([label, value], i) => ({ label, value, color: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }))

    // top selling products (needs order.items)
    const byProduct = {}
    live(cur).forEach((o) =>
      orderItems(o).forEach((it) => {
        const name = it.name ?? it.title ?? 'Product'
        const qty = Number(it.qty ?? it.quantity ?? 1)
        const price = Number(it.price ?? 0)
        const row = (byProduct[name] ||= {
          name,
          units: 0,
          revenue: 0,
          img: it.image ?? it.img,
          id: it.productId ?? it.product_id,
        })
        row.units += qty
        row.revenue += Number(it.total ?? price * qty)
      }),
    )
    const top = Object.values(byProduct)
      .sort((a, b) => b.units - a.units)
      .slice(0, 5)
      .map((r) => {
        const match = data.products.find((x) => String(x.id) === String(r.id) || x.name === r.name)
        return { ...r, img: r.img || match?.img }
      })

    // sales summary
    // NOTE: net = revenue - discounts + taxes + shipping. If your order "total"
    // already includes tax and shipping, change this to just `revenue - discounts`.
    const discounts = sum(live(cur), orderDiscount)
    const taxes = sum(live(cur), orderTax)
    const shipping = sum(live(cur), orderShipping)

    return {
      start,
      end: end - DAY,
      c,
      p,
      points,
      statuses,
      channels,
      top,
      cur,
      summary: { revenue: c.revenue, discounts, taxes, shipping, net: c.revenue - discounts + taxes + shipping },
    }
  }, [data, days, grain])

  if (loading) return <Loading />

  if (!stats) {
    return (
      <>
        <PageHead title='Reports' sub='Sales and order performance.' />
        <section className={card}>
          <p className='text-[13px] text-[#6b7280]'>
            Reports need the admin orders endpoint ({ROUTES.adminOrders}). Once it exists, your numbers show here.
          </p>
        </section>
      </>
    )
  }

  const { c, p, summary } = stats
  const prevLabel = `previous ${days} days`
  const statusTotal = stats.statuses.reduce((s, x) => s + x.value, 0)
  const channelTotal = stats.channels.reduce((s, x) => s + x.value, 0)
  const share = (v, t) => (t ? `${((v / t) * 100).toFixed(1)}%` : '0%')

  const exportReport = () =>
    downloadCsv(
      [
        ['Order no', 'Date', 'Status', 'Total'],
        ...stats.cur.map((o) => [o.orderNo, fmtLong(orderTime(o)), o.status, orderTotal(o)]),
      ],
      `report-${fmtDay(stats.start)}-${fmtDay(stats.end)}.csv`,
    )

  return (
    <div>
      <PageHead
        title='Reports'
        sub={`${fmtLong(stats.start)} - ${fmtLong(stats.end)}`}
        actions={
          <>
            <select className={selectCls} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label='Date range'>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              type='button'
              onClick={exportReport}
              className='inline-flex items-center gap-2 h-[38px] px-4 bg-[#ff6b4a] hover:brightness-95 text-white text-[13px] font-semibold rounded-lg transition'
            >
              <FiDownload /> Export report
            </button>
          </>
        }
      />

      {/* KPI cards */}
      <div className='grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-3.5 mb-4'>
        <Kpi icon={RupeeIcon} tone='blue' label='Total Revenue' value={money(c.revenue)} change={pct(c.revenue, p.revenue)} prevLabel={prevLabel} />
        <Kpi icon={FiShoppingCart} tone='green' label='Total Orders' value={c.orders.toLocaleString('en-IN')} change={pct(c.orders, p.orders)} prevLabel={prevLabel} />
        <Kpi icon={FiUsers} tone='purple' label='Total Customers' value={c.customers.toLocaleString('en-IN')} change={pct(c.customers, p.customers)} prevLabel={prevLabel} />
        <Kpi icon={FiBox} tone='amber' label='Avg. Order Value' value={money(Math.round(c.aov))} change={pct(c.aov, p.aov)} prevLabel={prevLabel} />
        <Kpi icon={FiRotateCcw} tone='red' label='Return Rate' value={`${c.returnRate.toFixed(2)}%`} change={p.returnRate ? c.returnRate - p.returnRate : null} goodWhenDown prevLabel={prevLabel} />
      </div>

      {/* Sales overview + channel */}
      <div className='grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-4 mb-4'>
        <section className={card}>
          <div className='flex items-center justify-between mb-1.5'>
            <h2 className='text-[15px] font-semibold text-[#1f2937]'>Sales Overview</h2>
            <select
              className={`${selectCls} !h-8 !text-[12px]`}
              value={grain}
              onChange={(e) => setGrain(e.target.value)}
              aria-label='Group by'
            >
              <option value='daily'>Daily</option>
              <option value='weekly'>Weekly</option>
            </select>
          </div>
          <div className='flex justify-end gap-4 text-[12px] text-[#6b7280]'>
            <span className='flex items-center'>
              <i className='inline-block w-2.5 h-2.5 rounded-full mr-1.5' style={{ background: COLORS.accent }} /> Revenue (₹)
            </span>
            <span className='flex items-center'>
              <i className='inline-block w-2.5 h-2.5 rounded-full mr-1.5' style={{ background: COLORS.blue }} /> Orders
            </span>
          </div>
          <LineChart points={stats.points} />
        </section>

        <section className={card}>
          <h2 className={cardTitle}>Sales by Channel</h2>
          {channelTotal === 0 ? (
            <Empty title='No sales in this period' />
          ) : (
            <div className='flex items-center gap-5 flex-wrap'>
              <Donut segments={stats.channels} total={money(channelTotal)} caption='Total' />
              <Legend rows={stats.channels.map((s) => ({ ...s, value: money(s.value), share: share(s.value, channelTotal) }))} />
            </div>
          )}
        </section>
      </div>

      {/* Top products + status + summary */}
      <div className='grid grid-cols-1 lg:grid-cols-[1.3fr_1.1fr_1fr] gap-4 mb-4'>
        <section className={card}>
          <h2 className={cardTitle}>Top Selling Products</h2>
          {stats.top.length === 0 ? (
            <p className='text-[13px] text-[#6b7280] leading-relaxed'>
              Top products need each order to include its items (<code>items</code> with name, qty, price).
            </p>
          ) : (
            <table className='w-full border-collapse text-[13px]'>
              <thead>
                <tr className='bg-[#f7f8fa] text-[11px] font-medium text-[#6b7280]'>
                  <th className='text-left font-medium px-2.5 py-2'>Product</th>
                  <th className='text-right font-medium px-2.5 py-2 whitespace-nowrap'>Units Sold</th>
                  <th className='text-right font-medium px-2.5 py-2'>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {stats.top.map((t) => (
                  <tr key={t.name} className='border-b border-[#f0f2f5] text-[#1f2937]'>
                    <td className='px-2.5 py-2.5'>
                      <span className='flex items-center gap-2.5'>
                        {t.img ? (
                          <img src={imgUrl(t.img)} alt='' className='w-7 h-7 rounded-md object-cover bg-[#eef0f3] shrink-0' />
                        ) : (
                          <span className='w-7 h-7 rounded-md bg-[#eef0f3] shrink-0' />
                        )}
                        {t.name}
                      </span>
                    </td>
                    <td className='px-2.5 py-2.5 text-right whitespace-nowrap'>{t.units}</td>
                    <td className='px-2.5 py-2.5 text-right whitespace-nowrap'>{money(t.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Link className={linkBtn} to='/admin/products'>View All Products</Link>
        </section>

        <section className={card}>
          <h2 className={cardTitle}>Orders Status</h2>
          {statusTotal === 0 ? (
            <Empty title='No orders in this period' />
          ) : (
            <div className='flex items-center gap-5 flex-wrap'>
              <Donut segments={stats.statuses} total={statusTotal.toLocaleString('en-IN')} caption='Total' />
              <Legend rows={stats.statuses.map((s) => ({ ...s, share: share(s.value, statusTotal) }))} />
            </div>
          )}
          <Link className={linkBtn} to='/admin/orders'>View All Orders</Link>
        </section>

        <section className={card}>
          <h2 className={cardTitle}>Sales Summary</h2>
          <dl className='text-[13px]'>
            <div className='flex justify-between px-2.5 py-3'>
              <dt>Total Revenue</dt>
              <dd>{money(summary.revenue)}</dd>
            </div>
            <div className='flex justify-between px-2.5 py-3'>
              <dt>Total Discounts</dt>
              <dd className='text-[#ef4444]'>- {money(summary.discounts)}</dd>
            </div>
            <div className='flex justify-between px-2.5 py-3'>
              <dt>Total Taxes</dt>
              <dd>{money(summary.taxes)}</dd>
            </div>
            <div className='flex justify-between px-2.5 py-3'>
              <dt>Shipping Charges</dt>
              <dd>{money(summary.shipping)}</dd>
            </div>
            <div className='flex justify-between px-2.5 py-3 mt-1.5 bg-[#f3f5f7] rounded-lg font-semibold'>
              <dt>Net Revenue</dt>
              <dd className='text-[#22b35e]'>{money(summary.net)}</dd>
            </div>
          </dl>
          <Link className={linkBtn} to='/admin/orders'>View Full Summary</Link>
        </section>
      </div>
    </div>
  )
}
